/**
 * API client for the account-wide telephony configuration: the Twilio and
 * transcription credentials, the retention clock, working hours, the spoken
 * notice with the voices that record it, the diagnostics that say what is
 * broken, and the caller ids a call may go out as.
 *
 * This is the Settings surface. What one call does is `lib/calls.ts`, and the
 * Twilio-side recording storage is `lib/callRecordings.ts`. All of it is
 * platform-scoped behind `manage:crm.calls` - and since the §3.8 split that is
 * a narrower group than "everybody who telephones people", so nothing on an
 * operator's own working surface may read from this file. The four booleans the
 * dial pad used to take from here have their own endpoint (`lib/dialCaps.ts`)
 * for exactly that reason.
 */
import { api } from "./api";
import type { CallDivertSettings } from "./callDivert";
import type { CallWindowPrefs } from "./callWindowPrefs";
import type { TelephonyAiAgentPatch, TelephonyAiAgentStatus } from "./telephonyAgent";

export interface TelephonyStatus {
  twilio: {
    accountSid: string;
    apiKeySid: string;
    twimlAppSid: string;
    pushCredentialSid: string;
    callerId: string;
    region: string;
    record: boolean;
    /** Seconds the customer's phone rings on a call we place, both bridges. */
    outboundRingSeconds: number;
    purgeOrphanRecordings: boolean;
    edge: string;
    /** Bridge new outbound calls as a conference, which is what makes hold possible. */
    conference: boolean;
    /** What a held customer hears; empty is Twilio's default hold music. */
    holdUrl: string;
    /** What the operator hears on a call coming in; empty is the built-in ring. */
    ringtoneUrl: string;
    /** What the operator hears while a call they placed rings; empty is Twilio's own. */
    ringbackUrl: string;
    /** Split a per-leg conference recording into two labelled speaker channels. */
    legDualSplit: boolean;
    authTokenSet: boolean;
    apiKeySecretSet: boolean;
    configured: boolean;
  };
  /**
   * Which outside service does the speech work, and what that makes possible.
   *
   * The three booleans are named for the JOB rather than for a vendor, which is
   * the whole point of the switch: "can a call be transcribed" stopped being the
   * same question as "is there a Gemini key" the moment there were two services.
   */
  providers: {
    /** Who turns a finished recording into text. */
    transcribe: TranscribeProvider;
    /** Who reads an announcement out loud when a clip is generated. */
    tts: TtsProvider;
    /** The chosen transcription service has a key. */
    transcribeReady: boolean;
    /**
     * A summary, next steps and a voicemail's department can be produced.
     *
     * Separate from `transcribeReady` because with ElevenLabs chosen the two
     * come apart: Scribe writes the verbatim transcript and the reading of it
     * stays with Gemini. False here with `transcribeReady` true is a real state
     * - transcripts arrive, summaries are blank - and the page has to say so.
     */
    summaryReady: boolean;
    /** An announcement clip can be synthesised. Always true on the free provider. */
    ttsReady: boolean;
    /**
     * The chosen ElevenLabs synthesis model cannot speak Estonian.
     *
     * Worth its own flag because the failure is silent: the wrong model returns
     * fluent audio of Estonian words in another language's mouth, which somebody
     * can preview, approve and put on the phone.
     */
    ttsModelLacksEstonian: boolean;
  };
  gemini: {
    model: string;
    apiKeySet: boolean;
    /**
     * Gemini requests allowed per Pacific day, both consumers together.
     * ZERO MEANS NO CEILING - see the server's `TelephonyGemini`.
     */
    dailyRequestCap: number;
    /** How many of those are held back for the after-call transcript pass. */
    transcribeReserve: number;
  };
  elevenlabs: {
    /** Speech-to-text model id, used when `providers.transcribe` is "elevenlabs". */
    sttModel: string;
    /** Synthesis model id, used when `providers.tts` is "elevenlabs". */
    ttsModel: string;
    /**
     * Which workspace voice an announcement picker opens on, as the bare
     * ElevenLabs id. "" means whatever the workspace lists first.
     *
     * A default, not a restriction: every picker offers both synthesis services
     * at once and any announcement may be recorded in any voice - see
     * `TtsVoice.provider`.
     */
    voiceId: string;
    apiKeySet: boolean;
  };
  /** The live co-pilot. No secrets in this group, so it comes back as stored. */
  pilot: {
    enabled: boolean;
    windowSec: number;
    maxWindows: number;
    /** Run the co-pilot on calls that carry no spoken announcement. */
    allowWithoutNotice: boolean;
    /** Switched on, a Gemini key present AND a notice that plays. */
    ready: boolean;
    /** Which of those three is missing. */
    blocker: PilotBlocker;
    /** The chosen speech service has no key, so no window can be heard. */
    needsKey: boolean;
    /**
     * Heard but not read back: no Gemini key, so the panel gets a transcript
     * and no suggestion cards. Not a blocker - see the card's own note.
     */
    noCoach: boolean;
  };
  /** The shape a call window opens in on a browser with no preference yet. */
  window: CallWindowPrefs;
  /** Calls AI (`lib/telephonyAgent.ts`): the stored group plus whether it would really answer. */
  aiAgent: TelephonyAiAgentStatus;
  /** Calls coming IN to the Twilio number, answered in this browser. */
  inbound: {
    enabled: boolean;
    ringSeconds: number;
    /**
     * Refuse a caller who withheld their number.
     *
     * The one part of the blocklist that is a SETTING rather than a row: a
     * withheld number is not a number, so it cannot be written down as one. The
     * blocked numbers themselves live on their own screen behind
     * `/calls/blocklist`, because the person who can tell a robot from a
     * customer is the operator, and Settings is behind a wider key.
     */
    blockAnonymous: boolean;
    /**
     * Park the caller in a room with our hold music instead of the ringing
     * tone, and ring at most two operators into it
     * (`docs/handoff-crm-sissetulev-konverents.md`).
     */
    conference: boolean;
    /**
     * The switch is on but there is no hold music to play, so nobody is
     * parked: the server keeps such a call on the ordinary bridge rather than
     * play a stranger's music. Said on the card, because from the phone it
     * sounds exactly like the switch being off.
     */
    conferenceSilent: boolean;
    voicemail: boolean;
    /**
     * Where the call goes when the desk does not answer, E.164, "" for none.
     *
     * It used to ring ALONGSIDE the browsers, in the same dial, so every
     * inbound call also placed an outbound leg. It is a second stage now: the
     * desk gets its full ring time first and this number is only tried if
     * nobody picked up.
     */
    forwardTo: string;
    /** Seconds that number may ring. Its own setting, because a mobile rings
     *  until the carrier answers it - and that is a personal voicemail. */
    forwardRingSeconds: number;
    /**
     * Who a call answered on a phone away from the desk belongs to, or "" to
     * leave it unowned.
     *
     * Both away-from-the-desk phones: the forwarded one and the diverted one.
     * Neither has a CRM session behind it, so nothing claims the call and the
     * row keeps the empty owner it was created with.
     */
    mobileOwnerUserId: string;
    voicemailPrompt: NoticeVariant;
    unavailablePrompt: NoticeVariant;
    /** A caller nobody knows becomes a juhuklient when they leave a message. */
    voicemailLead: boolean;
    /** The message becomes a support ticket, using the AI summary. */
    voicemailTicket: boolean;
    /** When the number is answered by a person, and what is said outside that. */
    hours: TelephonyHoursSettings;
    /**
     * Sending calls somewhere else BEFORE anybody is rung, by hand or by the
     * clock.
     *
     * Not `forwardTo` above, and the difference is the point: that number rings
     * alongside the browsers on every call, this one replaces them. The call log
     * throws the manual half of it through its own endpoint (`lib/callDivert.ts`).
     */
    divert: CallDivertSettings;
    /** The keys a caller may press before anybody is rung. */
    menu: CallMenuSettings;
    /** Switched on AND Twilio configured - the phone would really be answered. */
    ready: boolean;
    /**
     * An after-hours caller would hear something rather than nothing.
     *
     * The one state on the working-hours card that cannot be found by ringing
     * the number at the wrong time: the gate on with the wording still empty
     * ends the call silently, which sounds exactly like the gate being off.
     */
    afterHoursPlays: boolean;
    /**
     * The forwarding number is our own line.
     *
     * A call sent there comes straight back to the same webhook and is forwarded
     * again. The server refuses it at dial time, but a settings page that saves
     * a number and then quietly ignores it is the kind of bug that costs an
     * afternoon.
     */
    divertSelf: boolean;
    /**
     * The menu would really play, rather than holding the caller in silence.
     *
     * The one state on the menu card that cannot be found by ringing the number:
     * the switch on with the question still unwritten `<Gather>`s in silence,
     * waits, times out and rings - which sounds exactly like the switch being
     * off, only slower.
     */
    menuPlays: boolean;
  };
  /** How long audio and verbatim transcripts are kept. No secrets, stored as read. */
  retention: TelephonyRetentionSettings;
  /** Whether Twilio is asked about a number before it is dialled. Both off by default. */
  lookup: TelephonyLookupSettings;
  /**
   * How many calls the sweep would take right now, at the ages above.
   *
   * Counted whether or not the switch is on - the number matters most to the
   * person deciding whether to throw it. Null when the count itself failed,
   * which the card must render as "not known": a zero shown because a query
   * broke is how somebody enables a sweep believing it has nothing to do.
   */
  retentionDue: RetentionDue | null;
  notice: TelephonyNoticeSettings & {
    /** An announcement would really play on the calls this config PLACES. */
    outboundPlays: boolean;
    /** ...and on a call ringing in to the Twilio number. */
    inboundPlays: boolean;
    /** Twilio has a voice for `language`. */
    languageVoiceable: boolean;
    /** Tags Twilio has a voice for; absent from an API older than the language picker. */
    sayLanguages?: string[];
    /** A text is configured in a language Twilio cannot read - record a clip. */
    textNeedsClip: boolean;
    /**
     * Per language: does anything on that tab still need a recording made?
     *
     * Optional in this mirror for the reason `locales` is: a status from a
     * build that predates the field must render as "nothing to warn about"
     * rather than as a blank card.
     */
    localeNeedsClip?: Partial<Record<NoticeLocale, boolean>>;
    /** Which languages an outgoing call could actually be announced in. */
    outboundLocales?: NoticeLocale[];
  };
  /**
   * What is known about the synthesised recording behind each announcement.
   *
   * Absent for a slot whose address is the one that ships with the app or a URL
   * somebody pasted themselves - there is no voice to name and no script to
   * compare against.
   */
  clips: Partial<Record<string, NoticeClipInfo>>;
  /**
   * The address of the recording that ships with the API, per announcement.
   *
   * Read from the server rather than written out here so that "back to the
   * shipped recording" is one value the API owns. A front end holding its own
   * copy of four filenames is the same mistake as a hard-coded voice list: it
   * looks right until the day the API renames one.
   */
  builtInAudio: Record<NoticeSlot, string>;
  /**
   * The track behind the hold setting, when the operator uploaded one.
   *
   * Null for an empty setting or a pasted address - there is no file of ours to
   * describe. It carries its own `audioUrl` because the setting itself points
   * at a looping TwiML document: handing that to a player downloads XML instead
   * of playing music, so the address to listen to cannot be derived from the
   * field on screen.
   */
  holdMusic: HoldMusicInfo | null;
  /**
   * An uploaded track the hold setting does NOT point at.
   *
   * The one fact about this card the browser cannot reach on its own. An upload
   * fills in an unsaved field, and if anything eats that field before Save - a
   * second tab, the clear button, a Save pressed on the card next door, which
   * writes the row without ever carrying the hold group - the track is left in
   * the store referenced by nothing. The page then loads with an empty field,
   * indistinguishable from never having uploaded, while held callers keep
   * hearing Twilio's own music. This is what makes that visible and one click
   * to undo.
   */
  holdMusicStray: StrayHoldMusic | null;
  publicBaseUrl: string;
  voiceWebhookUrl: string;
  /**
   * The public privacy notice the spoken announcement points at.
   *
   * Built by the server (`lib/privacy/paths.ts`) rather than glued together
   * here from `publicBaseUrl` and a path this file would have to know. The
   * short address exists to be said out loud, so it will get shorter or move
   * again, and a copy of it in the browser is a copy that would stay behind.
   */
  privacyNoticeUrl: string;
  /**
   * Recording is on and no announcement would play - the shipped default pair.
   *
   * Computed by the server rather than derived here from `twilio.record` and
   * `notice.plays`, so the three panels that warn about it cannot each arrive at
   * a slightly different answer.
   */
  recordsWithoutNotice: boolean;
}

/** Mirrors the server's `TelephonyRetention`. */
export interface TelephonyRetentionSettings {
  /** Run the sweep. Off means the two ages below are a preview and nothing more. */
  enabled: boolean;
  /** Age in days at which a call's audio is deleted, ours and Twilio's copy. */
  recordingDays: number;
  /** Age in days at which the verbatim transcript and the live turns are removed. */
  transcriptDays: number;
}

/**
 * Asking Twilio about a number before dialling it.
 *
 * Two switches because the two halves are priced differently: checking that a
 * number is real is free, and asking what kind of line it is costs per query.
 * Both ship off.
 */
export interface TelephonyLookupSettings {
  /** Refuse a number Twilio does not recognise, before the call is placed. Free. */
  enabled: boolean;
  /** Also ask mobile or landline, and remember the answer. Billed per number. */
  lineType: boolean;
}

/** What the sweep would take at the ages currently saved. */
export interface RetentionDue {
  recordings: number;
  transcripts: number;
}


/**
 * The announcement types and the synthesis calls, moved to `telephonyNotice.ts`
 * and re-exported here so no import anywhere had to change. This file was over
 * the size limit AGENTS.md §4 sets and could not take another field.
 */
export type {
  NoticeSlot,
  NoticeLocale,
  NoticeLocaleOverlay,
  NoticeClipInfo,
  NoticeVariant,
  NoticeDirectionSettings,
  TelephonyNoticeSettings,
  NoticeDirectionPatch,
  TtsProvider,
  TtsVoice,
  TtsVoiceGroup,
  TtsVoiceListing,
  GeneratedClip,
} from "./telephonyNotice";
export { listTtsVoices, generateNoticeClip, NOTICE_LOCALES } from "./telephonyNotice";

/**
 * The working-hours gate, moved to `telephonyHours.ts` and re-exported here so
 * no import anywhere had to change. Same move the announcement types made above
 * and for the same reason: this file was at the size limit AGENTS.md section 4
 * sets and could not take another field.
 */
export type {
  BusinessDay,
  TelephonyHoursSettings,
  HoursPreview,
  TelephonyHoursPatch,
} from "./telephonyHours";
export { previewHours } from "./telephonyHours";
import type { TelephonyHoursSettings, TelephonyHoursPatch } from "./telephonyHours";
import type {
  NoticeSlot,
  NoticeLocale,
  NoticeVariant,
  NoticeDirectionSettings,
  TelephonyNoticeSettings,
  NoticeDirectionPatch,
  NoticeClipInfo,
  TtsProvider,
} from "./telephonyNotice";

/** One key on the phone and the team behind it. Mirrors `MenuDepartment`. */
export interface CallMenuDepartment {
  /** "1" to "9", as text: it is a key, and Twilio reports `Digits` as text. */
  digit: string;
  /**
   * The department name, stored exactly as a ticket would store it.
   *
   * Free text, like `Ticket.department` itself, and the combobox on the card is
   * seeded from the departments already in use so the two agree. A name that
   * does not match one an operator would type is a call no saved filter finds.
   */
  department: string;
}

/**
 * Mirrors the server's `TelephonyInboundMenu`.
 *
 * A HAND-WRITTEN MIRROR with nothing checking it against the server: a field
 * missed here compiles, renders and silently never saves. The three that hurt
 * most are the two prompts, because the card would show empty boxes that look
 * like an unwritten question rather than a lost one.
 */
export interface CallMenuSettings {
  enabled: boolean;
  askLanguage: boolean;
  timeoutSec: number;
  departments: CallMenuDepartment[];
  /** All three languages in one wording: the caller has not chosen one yet. */
  languagePrompt: NoticeVariant;
  /** Per-language, unlike the question above: by now the caller has chosen. */
  departmentPrompt: NoticeVariant;
}

/**
 * An uploaded hold track, as the server describes it.
 *
 * A different shape from `NoticeClipInfo` and not a variant of it: an
 * announcement is described by the voice that reads it and the script it
 * follows, and a music file has neither. What can be said about this one is
 * what it is called, how big it is, and where to press play.
 */
export interface HoldMusicInfo {
  id: string;
  /** The audio itself, for the player. Not the hold setting, which is TwiML. */
  audioUrl: string;
  /** The name of the uploaded file. May be empty on a very old row. */
  label: string;
  bytes: number;
  /** Only known for an upload we had to re-encode; never a guess. */
  durationMs: number | null;
  createdAt: string;
}

/** What one upload produced, for the field to point at and the player to load. */
export interface UploadedHoldMusic extends Omit<HoldMusicInfo, "createdAt"> {
  /** The value the hold setting takes: a document that loops the audio above. */
  holdUrl: string;
}

/**
 * A track in the store that the SAVED setting does not use.
 *
 * The same shape as a fresh upload plus the date, because the card treats the
 * two identically: both are "a file nobody is hearing yet, and here is the one
 * button that changes that". See `holdMusicStray` on the status.
 */
export interface StrayHoldMusic extends HoldMusicInfo {
  /** What the hold setting has to be set to in order to adopt this track. */
  holdUrl: string;
}

/**
 * Send one file up. It is stored immediately and played by nobody until the
 * card is saved, which is the same two-step the announcement clips follow: the
 * gap between the two presses is where the operator listens to what they chose.
 */
export function uploadHoldMusic(file: File, signal?: AbortSignal): Promise<UploadedHoldMusic> {
  const form = new FormData();
  form.append("file", file);
  return api.upload("/admin/crm/calls/hold-music", form, signal);
}

/** What the ringtone upload hands back. */
export interface UploadedRingtone {
  id: string;
  /** Where the browser loads it from. Goes straight into the setting. */
  ringtoneUrl: string;
  bytes: number;
  durationMs: number | null;
  label: string;
}

/**
 * Store a ringtone and get its address back.
 *
 * Storing is not choosing: the address has to be saved with the rest of the
 * telephony settings before a call rings with it, which is what gives the
 * operator a moment to press play and hear what they picked.
 */
export function uploadRingtone(file: File, signal?: AbortSignal): Promise<UploadedRingtone> {
  const form = new FormData();
  form.append("file", file);
  return api.upload("/admin/crm/calls/ringtone", form, signal);
}

/**
 * The same, for the ring on a call the operator PLACES.
 *
 * A second function rather than an argument, because the two are two addresses
 * and a caller passing the wrong flag would store one operator's choice in the
 * other's setting - silently, and only audible on the next call.
 */
export function uploadRingback(file: File, signal?: AbortSignal): Promise<UploadedRingtone> {
  const form = new FormData();
  form.append("file", file);
  return api.upload("/admin/crm/calls/ringback", form, signal);
}

/**
 * `no_notice` (the account has no wording) and `no_call_notice` (this one call
 * was placed with the announcement off) are both hard stops, kept apart because
 * only the first has a fix in Settings. `no_notice_waived` is the co-pilot
 * running anyway, because the exemption is switched on - not a blocker, but
 * reported on every call so the panel can keep saying this one announces nothing.
 *
 * The live panel imports this one rather than writing the union out again.
 * It used to hold its own copy in `callPilot.ts`, which the type system never
 * tied to this one, so a value added on the server had to be remembered in two
 * unrelated files or the panel would quietly fall through to the wrong message.
 */
export type PilotBlocker =
  | "disabled"
  | "no_key"
  | "no_notice"
  | "no_call_notice"
  | "no_notice_waived"
  /**
   * Somebody pressed stop on this call's recording, so the co-pilot is off it
   * too. The only value here that is not a fault: everything is configured, the
   * announcement played, and the person on the phone asked.
   */
  | "recording_stopped"
  | null;


export interface TelephonySettingsPatch {
  twilio?: Partial<{
    accountSid: string;
    authToken: string;
    apiKeySid: string;
    apiKeySecret: string;
    twimlAppSid: string;
    pushCredentialSid: string;
    callerId: string;
    region: string;
    record: boolean;
    outboundRingSeconds: number;
    purgeOrphanRecordings: boolean;
    edge: string;
    conference: boolean;
    holdUrl: string;
    ringtoneUrl: string;
    ringbackUrl: string;
    legDualSplit: boolean;
  }>;
  providers?: Partial<{ transcribe: TranscribeProvider; tts: TtsProvider }>;
  gemini?: Partial<{
    apiKey: string;
    model: string;
    dailyRequestCap: number;
    transcribeReserve: number;
  }>;
  elevenlabs?: Partial<{ apiKey: string; sttModel: string; ttsModel: string; voiceId: string }>;
  pilot?: Partial<{
    enabled: boolean;
    windowSec: number;
    maxWindows: number;
    allowWithoutNotice: boolean;
  }>;
  window?: Partial<CallWindowPrefs>;
  aiAgent?: TelephonyAiAgentPatch;
  inbound?: Partial<{
    enabled: boolean;
    ringSeconds: number;
    blockAnonymous: boolean;
    conference: boolean;
    voicemail: boolean;
    forwardTo: string;
    forwardRingSeconds: number;
    mobileOwnerUserId: string;
    voicemailPrompt: Partial<NoticeVariant>;
    unavailablePrompt: Partial<NoticeVariant>;
    voicemailLead: boolean;
    voicemailTicket: boolean;
    hours: TelephonyHoursPatch;
    /** Flat, so a card may send one field of it without clearing the rest. */
    divert: Partial<CallDivertSettings>;
    /**
     * Flat but for the rows, which are sent WHOLE or not at all - the same rule
     * `hours.days` follows. A form that posts two rows means there are two now,
     * not that the third should be left where it was.
     */
    menu: Partial<Omit<CallMenuSettings, "departments" | "languagePrompt" | "departmentPrompt">> & {
      departments?: CallMenuDepartment[];
      languagePrompt?: Partial<NoticeVariant>;
      departmentPrompt?: Partial<NoticeVariant>;
    };
  }>;
  notice?: Partial<{
    enabled: boolean;
    language: string;
    voice: string;
    outbound: NoticeDirectionPatch;
    inbound: NoticeDirectionPatch;
    /**
     * One language tab at a time, and inside it one field at a time. The server
     * merges what is absent rather than clearing it, which is what stops two
     * open tabs from overwriting each other.
     */
    locales: Partial<
      Record<
        NoticeLocale,
        { enabled?: boolean; slots?: Partial<Record<NoticeSlot, Partial<NoticeVariant>>> }
      >
    >;
  }>;
  retention?: Partial<TelephonyRetentionSettings>;
  lookup?: Partial<TelephonyLookupSettings>;
  publicBaseUrl?: string;
}

export interface TelephonyServiceTest {
  ok: boolean;
  detail?: string;
  accountStatus?: string;
  error?: string;
}

/** One account number whose voice traffic reaches our TwiML App. */
export interface BoundNumber {
  sid: string;
  phoneNumber: string;
  friendlyName: string;
}

/**
 * Whether a call to the Twilio number would really reach this installation.
 *
 * The only part of the connection test that asks about something no stored
 * setting knows: routing a number to the TwiML App, and the app to our webhook,
 * is console work done by hand. An account can hold perfect credentials, show
 * "answering" on this page, and never ring.
 */
export interface VoiceBindingResult {
  ok: boolean;
  /** The TwiML App's Voice Request URL is our own webhook address. */
  appUrlMatches: boolean;
  /** What the Twilio console actually holds there; "" when it could not be read. */
  appVoiceUrl: string;
  /** The app posts rather than GETs. */
  appMethodMatches: boolean;
  /** Numbers bound to the app. Empty is the failure this check exists for. */
  numbers: BoundNumber[];
  /** More numbers on the account than were inspected - `numbers` is a floor. */
  truncated: boolean;
  /** not_configured | no_twiml_app | app_not_found | twilio_upstream | timeout */
  error?: string;
}

/**
 * One line of the Twilio checklist.
 *
 * Three states, not two. "Could not check" never collapses into red: an
 * unreachable Twilio shown as "your caller id is gone" sends somebody to fix a
 * setting that was never broken.
 */
export interface DiagRow {
  state: "ok" | "bad" | "unknown";
  /** Machine reason; the sentence lives in the catalogues. */
  reason?: string;
  /** One value worth showing beside the row: a key SID, a region, a balance. */
  detail?: string;
}

/**
 * The four questions a green "Twilio OK" does not answer. Mirrors
 * apps/api/src/modules/crm-calls/lib/diagnostics.ts.
 */
export interface TelephonyDiagnostics {
  keyPair: DiagRow;
  region: DiagRow;
  callerId: DiagRow;
  account: DiagRow;
}

export interface TelephonyTestResult {
  twilio: TelephonyServiceTest;
  gemini: TelephonyServiceTest;
  /**
   * The ElevenLabs key, tested whether or not a switch currently points at it.
   *
   * The order somebody sets this up in is "paste the key, press test, then
   * switch over"; a test that stayed quiet until after the switch would make
   * them flip a live setting to find out whether the key works at all.
   */
  elevenlabs: TelephonyServiceTest;
  inbound: VoiceBindingResult;
  diagnostics: TelephonyDiagnostics;
}

export function getTelephonySettings(): Promise<TelephonyStatus> {
  return api.get("/admin/crm/calls/settings");
}

export function saveTelephonySettings(patch: TelephonySettingsPatch): Promise<TelephonyStatus> {
  return api.put("/admin/crm/calls/settings", patch);
}

/**
 * How many calls a sweep at THESE two ages would take, asked before saving them.
 *
 * `TelephonyStatus.retentionDue` answers the same question for the ages already
 * stored, which stops being the right answer the moment somebody edits a field -
 * and the moment that matters most is somebody shortening the window.
 */
export function previewRetention(days: {
  recordingDays: number;
  transcriptDays: number;
}): Promise<RetentionDue> {
  const q = new URLSearchParams({
    recordingDays: String(days.recordingDays),
    transcriptDays: String(days.transcriptDays),
  });
  return api.get(`/admin/crm/calls/retention/preview?${q}`);
}

export function testTelephony(): Promise<TelephonyTestResult> {
  return api.post("/admin/crm/calls/settings/test", {});
}

/**
 * Just the "is a number wired up to us" half.
 *
 * Its own endpoint because the Inbound card asks on every load, and the full
 * test would make Twilio authenticate and the Gemini key round-trip to Google
 * to answer a question about phone number routing.
 */
export function testInboundBinding(): Promise<VoiceBindingResult> {
  return api.post("/admin/crm/calls/settings/test/inbound", {});
}

// ── Speech synthesis ────────────────────────────────────────────────────────
//
// Twilio has no Estonian voice, so an announcement in Estonian has to arrive as
// a file. These two endpoints are what makes one without leaving the page: the
// chosen service speaks the text in a chosen voice, the API encodes it and keeps
// it, and the address of the result goes into the audio field. Nothing here runs
// during a call - a call plays a stored file.
//
// Two services since the provider switch: the University of Tartu's neural TTS,
// which is free and speaks Estonian, and ElevenLabs, which is keyed and offers a
// library plus whatever the workspace has cloned. The browser does not branch on
// which - it renders whatever the listing returns.
//
// BOTH ARE OFFERED AT ONCE and the chosen voice decides who speaks it. A voice
// id arrives provider-prefixed (`tartunlp:mari`), so one announcement can be
// recorded in the free Tartu speaker and the next in a cloned ElevenLabs voice
// without anybody moving an account-wide switch first.

/** Who reads a finished recording. Mirrors the API's `TranscribeProvider`. */
export type TranscribeProvider = "gemini" | "elevenlabs";

// ── Caller ids ──────────────────────────────────────────────────────────────

/**
 * Where the number came from. `owned` is a number bought on the Twilio account,
 * `verified` one somebody proved they answer by keying in a code. Both may be
 * shown as the caller, but only the second can be (or needs to be) verified.
 */
export type CallerIdKind = "owned" | "verified";

export interface CallerIdRow {
  sid: string;
  phoneNumber: string;
  friendlyName: string;
  kind: CallerIdKind;
}

export function listCallerIds(): Promise<{ callerIds: CallerIdRow[]; selected: string }> {
  return api.get("/admin/crm/calls/caller-ids");
}

export function startCallerIdVerification(phoneNumber: string): Promise<{ phoneNumber: string; validationCode: string }> {
  return api.post("/admin/crm/calls/caller-ids/verify", { phoneNumber });
}
